import { useMemo } from "react";
import { cn } from "@/lib/utils";

export function Avatar({
  name,
  email,
  src,
  size = "md",
  className,
}: {
  name?: string | null;
  email?: string | null;
  src?: string | null;
  size?: "xs" | "sm" | "md" | "lg" | "xl";
  className?: string;
}) {
  const initials = useMemo(() => {
    const source = (name?.trim() || email?.trim() || "??").trim();
    // A phone number as the name ("+1 (786) 833-3977"): show "#", not "+(".
    if (/^[+\d(][\d\s()+.-]*$/.test(source)) return "#";
    const parts = source.split(/\s+/);
    if (parts.length >= 2) return ((parts[0]?.[0] ?? "") + (parts[1]?.[0] ?? "")).toUpperCase();
    return source.slice(0, 2).toUpperCase();
  }, [name, email]);
  const gradientIdx = useMemo(() => {
    const seed = (name || email || "").split("").reduce((sum, c) => sum + c.charCodeAt(0), 0);
    return (seed % 6) + 1;
  }, [name, email]);
  const sizeClass = {
    xs: "h-6 w-6 text-[10px]",
    sm: "h-8 w-8 text-xs",
    md: "h-9 w-9 text-sm",
    lg: "h-12 w-12 text-base",
    xl: "h-16 w-16 text-lg",
  }[size];
  return (
    <div
      className={cn(
        "inline-flex items-center justify-center rounded-full font-semibold text-white shrink-0",
        !src && `avatar-gradient-${gradientIdx}`,
        sizeClass,
        className
      )}
    >
      {src ? (
        <img src={src} alt={name ?? email ?? "Avatar"} className="h-full w-full rounded-full object-cover" />
      ) : (
        <span className="select-none">{initials}</span>
      )}
    </div>
  );
}
